import { MessageSquare } from 'lucide-react'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import { SnackbarType } from '@/utils/snackbar'
import { openInQuo } from '../utils/quoLink'

/**
 * "Open in Quo" icon button — copies the message for the operator to paste, then opens the driver's
 * conversation in the operator's own Quo app. The message is never passed to Quo in the deep link.
 *
 * Tries openphone:// then quo://; when neither is handled (app not installed) a snackbar explains instead
 * of failing silently. The clipboard copy still works, so the operator can paste into Quo Web or another app.
 *
 * Face: an SMS icon with a small Quo brand mark riding its bottom-right corner — reads as "SMS, via Quo"
 * instead of a bare logo.
 */
export const QuoSmsButton = ({ phone, text }: { phone: string; text?: string }) => {
    const { showSnackbar } = useSnackbar()
    const { copy } = useCopyToClipboard({ onCopied: () => showSnackbar(SnackbarType.SUCCESS, 'Message copied. Paste it in Quo.') })
    return (
        <button
            type="button"
            onClick={() => {
                // Start the clipboard write in the click handler, then launch synchronously so the browser
                // still treats the custom-protocol navigation as a direct user action.
                void copy(text)
                openInQuo(phone, () =>
                    showSnackbar(SnackbarType.ERROR, 'Quo app not found on this computer — install the Quo desktop app to text from your own line.')
                )
            }}
            title="Copy message and open Quo"
            aria-label="Copy message and open this driver in Quo"
            className="relative flex size-7 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors hover:bg-accent-soft-hover"
        >
            <MessageSquare className="h-3.5 w-3.5" />
            <img src="/quo_icon.png" alt="Quo" className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-[3px] shadow-sm" />
        </button>
    )
}
